import { useState, useMemo, useCallback } from "react";
import { type UserStory } from "@/lib/projectStore";
import { format, addDays, differenceInDays, parseISO, isAfter, isBefore, startOfDay, max } from "date-fns";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CalendarIcon, AlertTriangle, ArrowRight, Link2, Zap, Clock } from "lucide-react";

/* ─── Constants ─── */
const POINTS_TO_DAYS: Record<number, number> = {
  1: 1, 2: 1.5, 3: 2, 5: 4, 8: 6, 13: 10, 21: 16,
};

function estimateDays(storyPoints?: number): number {
  if (!storyPoints) return 3;
  return POINTS_TO_DAYS[storyPoints] || Math.ceil(storyPoints * 0.8);
}

const priorityOrder: Record<string, number> = { must: 0, should: 1, could: 2, wont: 3 };

interface Props {
  stories: UserStory[];
  onChange: (stories: UserStory[]) => void;
  projectStartDate?: string;
  sprintLengthWeeks?: number;
  onSprintLengthChange?: (weeks: number) => void;
}

export default function TimelinePanel({ stories, onChange, projectStartDate, sprintLengthWeeks = 2, onSprintLengthChange }: Props) {
  const [selectedStoryId, setSelectedStoryId] = useState<string | null>(null);
  const [linkingFrom, setLinkingFrom] = useState<string | null>(null);

  const today = startOfDay(new Date());
  const baseDate = projectStartDate ? parseISO(projectStartDate) : today;

  /* ─── Auto-suggest dates for stories without them ─── */
  const autoSchedule = useCallback(() => {
    const sorted = [...stories].sort((a, b) => {
      const pa = priorityOrder[a.priority] ?? 2;
      const pb = priorityOrder[b.priority] ?? 2;
      return pa - pb;
    });

    let cursor = baseDate;
    const updated = sorted.map((story) => {
      const days = story.estimatedDays || estimateDays(story.storyPoints);
      const start = story.startDate ? parseISO(story.startDate) : cursor;
      const due = addDays(start, days);
      cursor = due;
      return {
        ...story,
        startDate: story.startDate || start.toISOString(),
        dueDate: story.dueDate || due.toISOString(),
        estimatedDays: days,
        originalDueDate: story.originalDueDate || due.toISOString(),
      };
    });
    onChange(updated);
  }, [stories, onChange, baseDate]);

  /* ─── Cascade: when a story's due date shifts, push dependents ─── */
  const cascadeDates = useCallback(
    (changedId: string, newDueDate: Date, currentStories: UserStory[]): UserStory[] => {
      let result = [...currentStories];

      // Find all stories that depend on the changed one
      const dependents = result.filter(
        (s) => s.dependencies?.includes(changedId)
      );

      for (const dep of dependents) {
        const depStart = dep.startDate ? parseISO(dep.startDate) : today;
        // If the new due date of parent is after dependent's start, push it
        if (isAfter(newDueDate, depStart)) {
          const newStart = addDays(newDueDate, 1);
          const days = dep.estimatedDays || estimateDays(dep.storyPoints);
          const newDue = addDays(newStart, days);
          const idx = result.findIndex((s) => s.id === dep.id);
          if (idx !== -1) {
            result[idx] = {
              ...result[idx],
              startDate: newStart.toISOString(),
              dueDate: newDue.toISOString(),
            };
            // Recursively cascade
            result = cascadeDates(dep.id, newDue, result);
          }
        }
      }
      return result;
    },
    [today]
  );

  const handleDateChange = (storyId: string, field: "startDate" | "dueDate", date: Date) => {
    let updated = stories.map((s) => {
      if (s.id !== storyId) return s;
      const newStory = { ...s, [field]: date.toISOString() };
      // If start date changed, auto-adjust due date
      if (field === "startDate") {
        const days = s.estimatedDays || estimateDays(s.storyPoints);
        newStory.dueDate = addDays(date, days).toISOString();
      }
      // If due date changed, recalc estimated days
      if (field === "dueDate" && s.startDate) {
        newStory.estimatedDays = differenceInDays(date, parseISO(s.startDate));
      }
      return newStory;
    });

    // Cascade to dependents
    if (field === "dueDate") {
      updated = cascadeDates(storyId, date, updated);
    } else {
      const story = updated.find((s) => s.id === storyId);
      if (story?.dueDate) {
        updated = cascadeDates(storyId, parseISO(story.dueDate), updated);
      }
    }

    onChange(updated);
  };

  const handleAddDependency = (fromId: string, toId: string) => {
    if (fromId === toId) return;
    onChange(
      stories.map((s) => {
        if (s.id !== toId) return s;
        const deps = s.dependencies || [];
        if (deps.includes(fromId)) return s;
        return { ...s, dependencies: [...deps, fromId] };
      })
    );
    setLinkingFrom(null);
  };

  const removeDependency = (storyId: string, depId: string) => {
    onChange(
      stories.map((s) => {
        if (s.id !== storyId) return s;
        return { ...s, dependencies: (s.dependencies || []).filter((d) => d !== depId) };
      })
    );
  };

  /* ─── Timeline rendering ─── */
  const scheduledStories = stories.filter((s) => s.startDate && s.dueDate);
  const unscheduledStories = stories.filter((s) => !s.startDate || !s.dueDate);

  const allDates = scheduledStories.flatMap((s) => [parseISO(s.startDate!), parseISO(s.dueDate!)]);
  const timelineStart = allDates.length > 0
    ? startOfDay(allDates.reduce((a, b) => (isBefore(a, b) ? a : b)))
    : today;
  const timelineEnd = allDates.length > 0
    ? startOfDay(addDays(allDates.reduce((a, b) => (isAfter(a, b) ? a : b)), 1))
    : addDays(today, 30);
  const totalDays = Math.max(differenceInDays(timelineEnd, timelineStart), 7);

  const storyById = new Map(stories.map((s) => [s.id, s]));

  /* ─── Sprint boundaries ─── */
  const sprintDays = sprintLengthWeeks * 7;
  const sprintColors = [
    "bg-accent/[0.06]",
    "bg-primary/[0.04]",
    "bg-accent/[0.06]",
    "bg-primary/[0.04]",
  ];
  const sprintBorderColors = [
    "text-accent",
    "text-primary",
    "text-accent",
    "text-primary",
  ];
  const sprints: { number: number; startPct: number; widthPct: number; label: string; bgClass: string; borderClass: string }[] = [];
  if (sprintDays > 0) {
    let sprintStart = 0;
    let sprintNum = 1;
    while (sprintStart < totalDays) {
      const sprintEnd = Math.min(sprintStart + sprintDays, totalDays);
      const colorIdx = (sprintNum - 1) % sprintColors.length;
      sprints.push({
        number: sprintNum,
        startPct: (sprintStart / totalDays) * 100,
        widthPct: ((sprintEnd - sprintStart) / totalDays) * 100,
        label: `S${sprintNum}`,
        bgClass: sprintColors[colorIdx],
        borderClass: sprintBorderColors[colorIdx],
      });
      sprintStart = sprintEnd;
      sprintNum++;
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider">
            Timeline
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            {scheduledStories.length} scheduled · {unscheduledStories.length} unscheduled ·{" "}
            {stories.reduce((a, s) => a + (s.storyPoints || 0), 0)} total pts
          </p>
        </div>
        <div className="flex gap-2">
          {linkingFrom && (
            <Badge variant="outline" className="text-xs bg-accent/10 text-accent border-accent/30 gap-1">
              <Link2 className="h-3 w-3" /> Click a story to link dependency
              <button onClick={() => setLinkingFrom(null)} className="ml-1 hover:text-destructive">✕</button>
            </Badge>
          )}
          {/* Sprint length toggle */}
          <div className="flex items-center border border-border rounded-md overflow-hidden">
            {[1, 2, 3, 4].map((w) => (
              <button
                key={w}
                onClick={() => onSprintLengthChange?.(w)}
                className={cn(
                  "px-2.5 py-1 text-[10px] font-mono transition-colors",
                  sprintLengthWeeks === w
                    ? "bg-accent text-accent-foreground font-bold"
                    : "text-muted-foreground hover:bg-secondary"
                )}
              >
                {w}w
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={autoSchedule} className="gap-1.5 text-xs">
            <Zap className="h-3.5 w-3.5" /> Auto-Schedule
          </Button>
        </div>
      </div>

      {/* Auto-suggest info */}
      <div className="bg-secondary/50 rounded-md p-3 text-xs text-secondary-foreground font-body space-y-1">
        <p className="font-semibold flex items-center gap-1.5">
          <Zap className="h-3.5 w-3.5 text-accent" /> Smart Scheduling
        </p>
        <p className="text-muted-foreground">
          Click "Auto-Schedule" to assign dates based on story points (1pt≈1d, 3pt≈2d, 5pt≈4d, 8pt≈6d, 13pt≈10d).
          Adjust any due date and dependent items will cascade automatically.
          Use <Link2 className="h-3 w-3 inline" /> to create dependencies between stories.
        </p>
      </div>

      {stories.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground text-sm">
          Add user stories in the "User Stories" tab first, then come here to schedule them.
        </div>
      ) : (
        <>
          {/* Gantt Chart */}
          {scheduledStories.length > 0 && (
            <div className="border border-border rounded-md overflow-x-auto">
            <div className="min-w-[640px]">
              {/* Sprint labels header */}
              {sprints.length > 1 && (
                <div className="border-b border-border flex">
                  <div className="w-52 shrink-0 border-r border-border px-3 py-1 flex items-center">
                    <span className="text-[9px] font-mono text-muted-foreground uppercase">
                      {sprintLengthWeeks}w sprints
                    </span>
                  </div>
                  <div className="flex-1 relative h-7">
                    {sprints.map((sprint) => (
                      <div
                        key={sprint.number}
                        className={cn(
                          "absolute top-0 bottom-0 flex items-center justify-center text-[10px] font-mono font-bold border-r border-border/40",
                          sprint.bgClass,
                          sprint.borderClass
                        )}
                        style={{ left: `${sprint.startPct}%`, width: `${sprint.widthPct}%` }}
                      >
                        <span className="flex items-center gap-1">
                          <span className="opacity-60">▎</span>
                          {sprint.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Header - date markers */}
              <div className="bg-secondary/50 border-b border-border flex">
                <div className="w-52 shrink-0 px-3 py-2 text-[10px] font-mono text-muted-foreground uppercase border-r border-border">
                  Story
                </div>
                <div className="flex-1 relative h-8">
                  {/* Sprint boundary lines in date header */}
                  {sprints.map((sprint) => (
                    <div
                      key={`hdr-${sprint.number}`}
                      className="absolute top-0 bottom-0 border-r border-accent/20"
                      style={{ left: `${sprint.startPct + sprint.widthPct}%` }}
                    />
                  ))}
                  {Array.from({ length: Math.min(totalDays + 1, 60) }).map((_, i) => {
                    if (totalDays > 30 && i % 7 !== 0) return null;
                    if (totalDays > 14 && totalDays <= 30 && i % 2 !== 0) return null;
                    const d = addDays(timelineStart, i);
                    const left = (i / totalDays) * 100;
                    return (
                      <span
                        key={i}
                        className="absolute text-[9px] font-mono text-muted-foreground top-2"
                        style={{ left: `${left}%` }}
                      >
                        {format(d, "MMM d")}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Rows */}
              {scheduledStories
                .sort((a, b) => parseISO(a.startDate!).getTime() - parseISO(b.startDate!).getTime())
                .map((story) => {
                  const start = parseISO(story.startDate!);
                  const end = parseISO(story.dueDate!);
                  const leftPct = (differenceInDays(start, timelineStart) / totalDays) * 100;
                  const widthPct = Math.max((differenceInDays(end, start) / totalDays) * 100, 2);
                  const isSelected = selectedStoryId === story.id;
                  const hasShifted = story.originalDueDate && story.dueDate !== story.originalDueDate;
                  const daysDiff = story.originalDueDate
                    ? differenceInDays(parseISO(story.dueDate!), parseISO(story.originalDueDate))
                    : 0;

                  return (
                    <div
                      key={story.id}
                      className={cn(
                        "flex border-b border-border last:border-b-0 hover:bg-secondary/20 transition-colors cursor-pointer",
                        isSelected && "bg-accent/5",
                        linkingFrom && linkingFrom !== story.id && "hover:bg-accent/10"
                      )}
                      onClick={() => {
                        if (linkingFrom && linkingFrom !== story.id) {
                          handleAddDependency(linkingFrom, story.id);
                        } else {
                          setSelectedStoryId(isSelected ? null : story.id);
                        }
                      }}
                    >
                      {/* Label */}
                      <div className="w-52 shrink-0 px-3 py-2 border-r border-border">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs font-semibold text-card-foreground truncate font-body">
                            {story.title}
                          </span>
                          {hasShifted && (
                            <Tooltip>
                              <TooltipTrigger>
                                <AlertTriangle className={cn("h-3 w-3 shrink-0", daysDiff > 0 ? "text-destructive" : "text-accent")} />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p className="text-xs">
                                  {daysDiff > 0 ? `Pushed ${daysDiff}d from original` : `Pulled ${Math.abs(daysDiff)}d earlier`}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          {story.storyPoints && (
                            <span className="text-[10px] font-mono text-muted-foreground">{story.storyPoints}pts</span>
                          )}
                          <span className="text-[10px] text-muted-foreground">
                            {story.estimatedDays || estimateDays(story.storyPoints)}d
                          </span>
                          {(story.dependencies?.length || 0) > 0 && (
                            <span className="text-[10px] text-accent flex items-center gap-0.5">
                              <Link2 className="h-2.5 w-2.5" />
                              {story.dependencies!.length}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Bar */}
                      <div className="flex-1 relative py-2 px-1">
                        {/* Sprint background bands */}
                        {sprints.map((sprint) => (
                          <div
                            key={`bg-${sprint.number}`}
                            className={cn(
                              "absolute top-0 bottom-0 border-r border-border/30",
                              sprint.bgClass
                            )}
                            style={{ left: `${sprint.startPct}%`, width: `${sprint.widthPct}%` }}
                          />
                        ))}
                        {/* Today marker */}
                        {differenceInDays(today, timelineStart) >= 0 && differenceInDays(today, timelineStart) <= totalDays && (
                          <div
                            className="absolute top-0 bottom-0 w-px bg-destructive/30 z-10"
                            style={{ left: `${(differenceInDays(today, timelineStart) / totalDays) * 100}%` }}
                          />
                        )}

                        {/* Original position ghost bar */}
                        {hasShifted && story.originalDueDate && (
                          <div
                            className="absolute h-5 top-1/2 -translate-y-1/2 rounded-sm bg-muted/40 border border-dashed border-muted-foreground/20"
                            style={{
                              left: `${leftPct}%`,
                              width: `${Math.max((differenceInDays(parseISO(story.originalDueDate), start) / totalDays) * 100, 1)}%`,
                            }}
                          />
                        )}

                        {/* Actual bar */}
                        <div
                          className={cn(
                            "absolute h-5 top-1/2 -translate-y-1/2 rounded-sm text-[9px] font-mono flex items-center px-1.5 overflow-hidden transition-all",
                            story.priority === "must"
                              ? "bg-accent text-accent-foreground"
                              : story.priority === "should"
                              ? "bg-primary/80 text-primary-foreground"
                              : "bg-secondary text-secondary-foreground",
                            hasShifted && daysDiff > 0 && "ring-1 ring-destructive/50"
                          )}
                          style={{
                            left: `${Math.max(leftPct, 0)}%`,
                            width: `${Math.min(widthPct, 100 - leftPct)}%`,
                          }}
                        >
                          <span className="truncate">
                            {format(start, "MMM d")} → {format(end, "MMM d")}
                          </span>
                        </div>

                        {/* Dependency arrows */}
                        {story.dependencies?.map((depId) => {
                          const dep = storyById.get(depId);
                          if (!dep?.dueDate) return null;
                          const depEndPct = (differenceInDays(parseISO(dep.dueDate), timelineStart) / totalDays) * 100;
                          return (
                            <div
                              key={depId}
                              className="absolute top-1/2 h-px bg-accent/40"
                              style={{
                                left: `${Math.min(depEndPct, leftPct)}%`,
                                width: `${Math.abs(leftPct - depEndPct)}%`,
                              }}
                            >
                              <ArrowRight className="h-2.5 w-2.5 text-accent/60 absolute right-0 -top-[4px]" />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
            </div>
            </div>
          )}

          {/* Detail panel for selected story */}
          {selectedStoryId && (() => {
            const story = storyById.get(selectedStoryId);
            if (!story) return null;
            const hasShifted = story.originalDueDate && story.dueDate !== story.originalDueDate;
            const daysDiff = story.originalDueDate && story.dueDate
              ? differenceInDays(parseISO(story.dueDate), parseISO(story.originalDueDate))
              : 0;

            return (
              <div className="border border-border rounded-md p-4 bg-card space-y-3 animate-fade-in">
                <div className="flex items-center justify-between">
                  <h3 className="font-display font-semibold text-sm">{story.title}</h3>
                  <div className="flex gap-1.5">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-[10px] gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        setLinkingFrom(selectedStoryId);
                      }}
                    >
                      <Link2 className="h-3 w-3" /> Add Dependency
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <DatePickerField
                    label="Start Date"
                    value={story.startDate ? parseISO(story.startDate) : undefined}
                    onChange={(d) => d && handleDateChange(selectedStoryId, "startDate", d)}
                  />
                  <DatePickerField
                    label="Due Date"
                    value={story.dueDate ? parseISO(story.dueDate) : undefined}
                    onChange={(d) => d && handleDateChange(selectedStoryId, "dueDate", d)}
                  />
                  <div>
                    <label className="text-[10px] font-mono text-muted-foreground uppercase block mb-1">
                      Est. Days
                    </label>
                    <span className="text-sm font-semibold font-body">
                      {story.estimatedDays || estimateDays(story.storyPoints)}d
                    </span>
                    <span className="text-[10px] text-muted-foreground ml-1">
                      ({story.storyPoints || 0} pts)
                    </span>
                  </div>
                  <div>
                    <label className="text-[10px] font-mono text-muted-foreground uppercase block mb-1">
                      Shift
                    </label>
                    {hasShifted ? (
                      <span className={cn("text-sm font-semibold font-body", daysDiff > 0 ? "text-destructive" : "text-accent")}>
                        {daysDiff > 0 ? `+${daysDiff}` : daysDiff} days
                      </span>
                    ) : (
                      <span className="text-sm text-muted-foreground">On track</span>
                    )}
                  </div>
                </div>

                {/* Dependencies */}
                {(story.dependencies?.length || 0) > 0 && (
                  <div>
                    <label className="text-[10px] font-mono text-muted-foreground uppercase block mb-1">
                      Depends On
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {story.dependencies!.map((depId) => {
                        const dep = storyById.get(depId);
                        return (
                          <Badge
                            key={depId}
                            variant="outline"
                            className="text-[10px] gap-1 pr-1"
                          >
                            {dep?.title || depId}
                            <button
                              onClick={() => removeDependency(story.id, depId)}
                              className="hover:text-destructive ml-0.5"
                            >
                              ✕
                            </button>
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Unscheduled stories */}
          {unscheduledStories.length > 0 && (
            <div>
              <h3 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Clock className="h-3 w-3" /> Unscheduled ({unscheduledStories.length})
              </h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {unscheduledStories.map((story) => (
                  <div
                    key={story.id}
                    className="border border-dashed border-border rounded-md p-3 bg-card/50 text-xs"
                  >
                    <span className="font-semibold font-body">{story.title}</span>
                    <span className="text-muted-foreground ml-2">
                      {story.storyPoints || 0}pts → ~{estimateDays(story.storyPoints)}d suggested
                    </span>
                  </div>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Click "Auto-Schedule" to assign dates to all unscheduled stories.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ─── Date Picker Field ─── */
function DatePickerField({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: Date;
  onChange: (d: Date | undefined) => void;
}) {
  return (
    <div>
      <label className="text-[10px] font-mono text-muted-foreground uppercase block mb-1">
        {label}
      </label>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className={cn(
              "w-full h-8 justify-start text-left text-xs font-normal",
              !value && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="h-3 w-3 mr-1.5" />
            {value ? format(value, "MMM d, yyyy") : "Pick date"}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={value}
            onSelect={onChange}
            initialFocus
            className={cn("p-3 pointer-events-auto")}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
